/**
 * Page 5 of the booking flow, the part with no screen (batch-29, item R12; plan §5.3, §5.5): the guest object a booking
 * is sent with, the replay key that is bound to it, and what each of the server's refusals does on the page. No figure
 * is worked out here — the total sent is the server's last quote.
 */
import type { BookingRequest, PublicEvent } from '../public/api';
import { businessDaysBefore } from './businessDays';
import { genderAsk } from './gender';
import { customKey, personsOf, requestsOf, type Slot } from './people';
import type { RequestField } from './requestFields';
import { CODE_MESSAGE, type Draft } from './state';
import { BAD_VOUCHER, normaliseCode } from './voucher';

const text = (v: string | undefined) => (v ?? '').trim();
/** Sent as nothing when left empty, as the one-page form sends them. */
const NULLED: readonly RequestField[] = ['estimated_arrival_window', 'floor_preference'];
const TICKS: readonly RequestField[] = ['accessibility_needs', 'early_checkin', 'late_checkout', 'quiet_room_pref', 'high_floor_pref', 'pet_traveling', 'first_time_visitor', 'connecting_rooms'];

/**
 * The guest object of a booking: the main guest's details, the request fields the organizer shows, the answers to the
 * organizer's own questions, the access code when the event asks for one, the consent lines ticked when the event has its own, and the booking exactly as it was priced with
 * everyone's name (and, on an event that asks it, everyone's gender). A room type preference is never sent — the rooms say it, and the server refuses both together.
 */
export function guestOf(
  event: Pick<PublicEvent, 'form' | 'registration_form_fields'> & { gender?: PublicEvent['gender'] }, draft: Pick<Draft, 'details' | 'names'>, slots: readonly Slot[][],
  request: Omit<BookingRequest, 'v' | 'mode'>, accessCode: string | null, consents: readonly string[] | null = null,
): Record<string, unknown> {
  const d = draft.details ?? {};
  const guest: Record<string, unknown> = { first_name: text(d.first_name), last_name: text(d.last_name), email: text(d.email), phone: text(d.phone) };
  for (const k of requestsOf(event.form).folded) guest[k] = TICKS.includes(k) ? d[k] === 'true' : NULLED.includes(k) ? text(d[k]) || null : text(d[k]);
  const custom: Record<string, string> = {};
  for (const q of event.registration_form_fields ?? []) { const v = text(d[customKey(q.id)]); if (v) custom[q.id] = v; }
  guest.custom_fields = custom;
  if (accessCode !== null) guest.access_code = accessCode.trim();
  // the ids of the organizer's consent lines the guest ticked (item PK5); not sent at all on an event with no lines
  if (consents !== null) guest.consents = [...consents];
  guest.booking = { v: 1, ...request, persons: personsOf(draft.names, slots, d, genderAsk(event.gender)) };
  // G3: a place in a shared room — the guest's own gender is the share bookingRooms() sent (the server checks they agree); no share, no key
  const share = request.rooms?.[0]?.share;
  if (share) guest.gender = share;
  return guest;
}

/** Everything of a submit the server's own replay check covers: the guest object (the booking is inside it), the promo code, the deposit choice and the voucher (only when one is applied). */
export const bodyOf = (ref: string, guest: Record<string, unknown>, coupon: string | null, payDeposit: boolean, voucher: string | null = null): string => {
  const v = voucher ? normaliseCode(voucher) : '';
  return JSON.stringify({ ref, guest, coupon: coupon ? coupon.toUpperCase() : null, payDeposit, ...(v ? { voucher: v } : {}) });
};

/** A short fingerprint of a text (53 bits, and its length). It only tells one body from another in this tab; nothing is secured by it. */
export function fingerprint(s: string): string {
  let a = 0xdeadbeef;
  let b = 0x41c6ce57;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 2654435761);
    b = Math.imul(b ^ c, 1597334677);
  }
  a = Math.imul(a ^ (a >>> 16), 2246822507) ^ Math.imul(b ^ (b >>> 13), 3266489909);
  b = Math.imul(b ^ (b >>> 16), 2246822507) ^ Math.imul(a ^ (a >>> 13), 3266489909);
  return `${s.length}.${(4294967296 * (2097151 & b) + (a >>> 0)).toString(36)}`;
}

/** The replay key in use and the body it belongs to. Held in memory for the tab; never stored. */
export interface HeldKey { hash: string; key: string }
/** The key for this body: the one in hand while the body is the same (a retry, a declined card), a new one after any change. */
export function replayKeyFor(held: HeldKey | null, body: string, make: () => string = () => crypto.randomUUID()): HeldKey {
  const hash = fingerprint(body);
  return held && held.hash === hash ? held : { hash, key: make() };
}

export const PRICES_CHANGED = 'Prices changed. Please review.';
export const TYPE_FILLED = 'That room type just filled up. Please choose another.';
export const START_AGAIN = 'Please start again.';
export const DECLINED = 'Your card was declined. Please try another card.';
export const BAD_PROMO = "That code isn't valid.";
export const NOT_COMPLETED = 'We could not complete your booking. Please try again.';

/** What a refusal does: a line on page 5, a return to an earlier page with the entries kept, or a new start with the draft cleared. */
export type Refusal =
  | { kind: 'stay'; line: string; reprice?: boolean; dropPromo?: boolean; dropVoucher?: boolean }
  | { kind: 'page'; step: 1 | 2 | 4; line: string; field?: 'code' } // `field`: the line is about the access code, and is said beside it too
  | { kind: 'restart'; line: string };

// page 1 holds the dates and the party: a party with nobody of the oldest group (0122) goes back there too
const TO_DATES = new Set(['dates_invalid', 'stay_too_short', 'stay_too_long', 'night_not_sold', 'adult_required']);
// G3: a shared place refused for the room itself (not offered, not this type, not a party of one), for its male/female
// answer or for the sharer's age is chosen again on page 2, where the shared place and that answer are asked
const TO_ROOMS = new Set(['hotel_required', 'room_unavailable', 'type_closed', 'does_not_fit', 'party_mismatch', 'room_unpriced',
  'share_off', 'share_type', 'share_one_person', 'share_gender_required', 'share_gender_mismatch', 'share_gender_unsupported', 'share_age']);

/** The server's answer to a submit that was not taken, as the page acts on it. Words the server sent are shown as sent. */
export function refusalOf(r: { error?: string; message?: string }, codeRequired: boolean): Refusal {
  const said = r.message?.trim() || NOT_COMPLETED;
  switch (r.error) {
    case 'total_mismatch': return { kind: 'stay', line: PRICES_CHANGED, reprice: true };
    case 'type_full': return { kind: 'page', step: 2, line: TYPE_FILLED };
    case 'invalid': case 'idempotency_conflict': return { kind: 'restart', line: START_AGAIN };
    case 'declined': return { kind: 'stay', line: DECLINED };
    case 'coupon': return { kind: 'stay', line: BAD_PROMO, dropPromo: true, reprice: true };
    case 'voucher': return { kind: 'stay', line: r.message?.trim() || BAD_VOUCHER, dropVoucher: true, reprice: true };
    case 'access_code': return { kind: 'page', step: 1, line: r.message?.trim() || CODE_MESSAGE, field: 'code' };
    case 'names_required': case 'gender_required': return { kind: 'page', step: 4, line: said };
    default: break;
  }
  // too many wrong codes is said at the code's own page; too many bookings is said here
  if (r.error === 'rate_limited' && codeRequired && !/submission/i.test(r.message ?? '')) return { kind: 'page', step: 1, line: said, field: 'code' };
  if (r.error && TO_DATES.has(r.error)) return { kind: 'page', step: 1, line: said };
  if (r.error && TO_ROOMS.has(r.error)) return { kind: 'page', step: 2, line: said };
  return { kind: 'stay', line: said };
}

/** The last day the names can be changed, when the event lets a guest change a booking: so many business days before arrival. */
export function namesUntil(event: Pick<PublicEvent, 'rules' | 'form'>, arrive: string | null | undefined): string | null {
  if (!event.rules?.allow_change || !arrive || !/^\d{4}-\d{2}-\d{2}$/.test(arrive)) return null;
  return businessDaysBefore(arrive, Number(event.form?.edit_deadline_days ?? 0) || 0);
}
